import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import profilePlaceholder from "../../assets/profile-placeholder.svg";
import AvatarStackCard from "../../components/card/avatarard";
import calendarIcon from "../../assets/calendar.png";
import { CheckCircle, ChevronRight, ChevronLeft, LogIn, LogOut, TriangleAlert, X } from "lucide-react";
import {
  FiCalendar,
  FiExternalLink,
  FiHash,
  FiLayers,
  FiMail,
  FiPhone,
  FiUser,
} from "react-icons/fi";
import useCurrentEmployee from "../../hooks/useCurrentEmployee";
import axios from "../../api/httpClient";
import { buildAssetUrl, getApiBaseUrl } from "../../data/authStorage";
import { getCurrentMonthBirthdays } from "../../api/birthdays";
import { getCurrentMonthAnniversaries } from "../../api/anniversaries";
import { listUpcomingHolidays } from "../../api/holidays";
import { listRequestApplications } from "../../api/requests";
import { deleteMyNotification, listMyNotifications, markMyNotificationAsRead } from "../../api/notifications";
import { checkInAttendance, checkOutAttendance, getTodayAttendance } from "../../api/attendance";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";
import TodoPanel from "../../components/todos/TodoPanel";
import NotificationDetailsModal from "../../components/notifications/NotificationDetailsModal";
import NotificationTypeIcon from "../../components/notifications/NotificationTypeIcon";
import { getNotificationActionLabel, getNotificationActionUrl } from "../../utils/notificationNavigation";
import ViewActionButton from "../../components/table/ViewActionButton";
import { getFreshLocationPayload } from "../../utils/geolocation";

const monthLabel = new Intl.DateTimeFormat("en-US", { month: "long" }).format(
  new Date(),
);
const DASHBOARD_CACHE_KEY = "cehr_employee_dashboard_swr_v1";
const ATTENDANCE_PROMPT_PREFIX = "cehr_attendance_prompt_dismissed";
const CHECKOUT_SNOOZE_MINUTES = 10;

const resolveImage = (imagePath) =>
  buildAssetUrl(imagePath) || imagePath || profilePlaceholder;

const getFullName = (person) =>
  `${person?.firstName || ""} ${person?.lastName || ""}`.trim() ||
  "Unnamed staff";

const getRoleLabel = (person) =>
  person?.positionName ||
  person?.position?.name ||
  person?.departmentName ||
  person?.department?.name ||
  "Staff";

const getEngagementImage = (post) => {
  const imageAttachment = (post?.attachments || []).find(
    (attachment) => attachment.type === "Image",
  );
  return buildAssetUrl(imageAttachment?.url);
};

const getExcerpt = (text = "") => {
  const value = `${text || ""}`.replace(/\s+/g, " ").trim();
  return value.length > 96 ? `${value.slice(0, 96)}...` : value;
};

const formatHolidayDate = (value) => {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
  });
};

const getLocalDayKey = (value = new Date()) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getAttendancePromptKey = (employeeId, action = "check-in", attendanceDay) => {
  return `${ATTENDANCE_PROMPT_PREFIX}_${action}_${employeeId || "unknown"}_${attendanceDay || getLocalDayKey()}`;
};

const getCheckoutSnoozeKey = (employeeId, attendanceDay) =>
  `${ATTENDANCE_PROMPT_PREFIX}_check-out-snoozed-until_${employeeId || "unknown"}_${attendanceDay || getLocalDayKey()}`;

const getCheckoutNotificationKey = (employeeId, attendanceDay) =>
  `${ATTENDANCE_PROMPT_PREFIX}_check-out-browser-notified_${employeeId || "unknown"}_${attendanceDay || getLocalDayKey()}`;

const hasReachedScheduleTime = (timeValue) => {
  if (!timeValue) {
    return false;
  }

  const [hourValue, minuteValue] = `${timeValue}`.split(":").map(Number);

  if (!Number.isFinite(hourValue) || !Number.isFinite(minuteValue)) {
    return false;
  }

  const now = new Date();
  const scheduledTime = new Date();
  scheduledTime.setHours(hourValue, minuteValue, 0, 0);

  return now >= scheduledTime;
};

function AttendanceFeedbackModal({ feedback, onClose }) {
  if (!feedback) return null;

  const isSuccess = feedback.type === "success";

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/40 px-4 pt-6 sm:pt-10">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <button
          type="button"
          onClick={onClose}
          className="float-right rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          aria-label="Close notification"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="mt-4 flex flex-col items-center text-center">
          <div className={`flex h-14 w-14 items-center justify-center rounded-full ${isSuccess ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
            {isSuccess ? <CheckCircle className="h-8 w-8" /> : <TriangleAlert className="h-8 w-8" />}
          </div>
          <h2 className="mt-4 text-xl font-semibold text-[#071C50]">{feedback.title}</h2>
          <p className="mt-2 text-sm leading-6 text-slate-500">{feedback.message}</p>
          <button
            type="button"
            onClick={onClose}
            className="mt-6 w-full rounded-lg bg-[#1234CD] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#0f2fb8]"
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const statusClass = (status) => {
  if (status === "Declined" || status === "Rejected") return "text-[#900B09]";
  if (status === "Pending") return "text-[#EA9620]";
  return "text-[#23BE18]";
};

const formatRelativeTime = (value) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const diffInSeconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  const units = [
    { label: "day", seconds: 86400 },
    { label: "hr", seconds: 3600 },
    { label: "min", seconds: 60 },
  ];
  const unit = units.find((item) => diffInSeconds >= item.seconds);

  if (!unit) {
    return "Just now";
  }

  const valueCount = Math.floor(diffInSeconds / unit.seconds);
  return `${valueCount}${unit.label}${unit.label === "day" && valueCount > 1 ? "s" : ""} ago`;
};

const getNotificationDate = (notification) =>
  notification.deliveredAt ||
  notification.sentAt ||
  notification.createdAt ||
  new Date().toISOString();

const getSettledValue = (result, fallback = {}) =>
  result.status === "fulfilled" ? result.value : fallback;

const Dashhboard = () => {
  const navigate = useNavigate();
  const cachedSnapshot = useMemo(
    () => readSessionCache(DASHBOARD_CACHE_KEY, null),
    [],
  );
  const [birthdayCelebrants, setBirthdayCelebrants] = useState(
    cachedSnapshot?.birthdayCelebrants || [],
  );
  const [workAnniversaryCelebrants, setWorkAnniversaryCelebrants] = useState(
    cachedSnapshot?.workAnniversaryCelebrants || [],
  );
  const [recentRequests, setRecentRequests] = useState(
    cachedSnapshot?.recentRequests || [],
  );
  const [recentEngagementPosts, setRecentEngagementPosts] = useState(
    cachedSnapshot?.recentEngagementPosts || [],
  );
  const [upcomingHolidays, setUpcomingHolidays] = useState(
    cachedSnapshot?.upcomingHolidays || [],
  );
  const [recentNotifications, setRecentNotifications] = useState(
    cachedSnapshot?.recentNotifications || [],
  );
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [currentHolidayIndex, setCurrentHolidayIndex] = useState(0);
  const [loading, setLoading] = useState(!cachedSnapshot);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const dashboardDataRef = useRef({
    birthdayCelebrants: cachedSnapshot?.birthdayCelebrants || [],
    workAnniversaryCelebrants: cachedSnapshot?.workAnniversaryCelebrants || [],
    recentRequests: cachedSnapshot?.recentRequests || [],
    recentEngagementPosts: cachedSnapshot?.recentEngagementPosts || [],
    upcomingHolidays: cachedSnapshot?.upcomingHolidays || [],
    recentNotifications: cachedSnapshot?.recentNotifications || [],
  });

  const {
    currentEmployee,
    currentEmployeeName,
    currentEmployeeEmail,
    currentEmployeePhoto,
    loading: profileLoading,
  } = useCurrentEmployee();
  const currentEmployeeId = currentEmployee?.id || "";
  const [attendanceToday, setAttendanceToday] = useState(null);
  const [attendanceDay, setAttendanceDay] = useState("");
  const [attendanceSettings, setAttendanceSettings] = useState(null);
  const [attendanceIsWorkDay, setAttendanceIsWorkDay] = useState(false);
  const [showAttendancePrompt, setShowAttendancePrompt] = useState(false);
  const [showCheckoutPrompt, setShowCheckoutPrompt] = useState(false);
  const [checkoutReminderTick, setCheckoutReminderTick] = useState(Date.now());
  const [notificationPermission, setNotificationPermission] = useState(() =>
    typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported",
  );
  const [attendanceActionLoading, setAttendanceActionLoading] = useState(false);
  const [attendanceFeedback, setAttendanceFeedback] = useState(null);
  const isCurrentlyOnLeave = Boolean(
    attendanceToday?.isOnLeave ||
    attendanceToday?.onLeave ||
    attendanceToday?.leave?.isActive ||
    attendanceToday?.currentLeave ||
    ["on leave", "leave"].includes(String(attendanceToday?.status || "").toLowerCase().replace(/[_-]+/g, " ")),
  );

  const employeeProfile = useMemo(() => {
    if (!currentEmployee) {
      return {
        name: "Employee",
        title: "Staff",
        employeeId: "-",
        level: "-",
        department: "-",
        email: "-",
        phone: "-",
        joined: "-",
        summary: "",
        photo: profilePlaceholder,
      };
    }

    const departmentNames = Array.from(new Set([
      ...(Array.isArray(currentEmployee.departments)
        ? currentEmployee.departments.map((department) => department?.name)
        : []),
      currentEmployee.department?.name,
      currentEmployee.departmentName,
    ].filter(Boolean)));
    // Keep the compact dashboard profile card bounded even when an employee
    // has many department assignments. The full assignments remain available
    // on the profile page.
    const visibleDepartmentNames = departmentNames.slice(0, 2);
    const departmentsText = visibleDepartmentNames.join(", ") || "-";

    return {
      name: currentEmployeeName,
      title:
        currentEmployee.position?.name ||
        currentEmployee.positionName ||
        currentEmployee.jobTitle ||
        "Staff",
      employeeId: currentEmployee.employeeNumber || currentEmployee.idNo || "-",
      level: currentEmployee.level?.name || currentEmployee.levelName || "-",
      department: departmentsText,
      email:
        currentEmployee.officialEmail || currentEmployee.personalEmail || "-",
      phone: currentEmployee.phone || "-",
      joined: currentEmployee.dateJoined
        ? new Date(currentEmployee.dateJoined).toLocaleDateString()
        : "-",
      summary:
        currentEmployee.profileSummary ||
        `Works in ${visibleDepartmentNames.length ? visibleDepartmentNames.join(", ") : "the organization"} as ${currentEmployee.position?.name || currentEmployee.positionName || currentEmployee.jobTitle || "a staff member"}.`,
      photo: resolveImage(
        currentEmployeePhoto || currentEmployee.profilePhotoUrl,
      ),
    };
  }, [currentEmployee, currentEmployeeName, currentEmployeePhoto]);

  const visibleHolidays = useMemo(() => {
    if (!upcomingHolidays.length) {
      return [];
    }

    return Array.from({ length: Math.min(2, upcomingHolidays.length) }, (_, offset) => offset)
      .map((offset) => upcomingHolidays[(currentHolidayIndex + offset) % upcomingHolidays.length])
      .filter(Boolean);
  }, [currentHolidayIndex, upcomingHolidays]);

  const goToNextHoliday = () => {
    if (upcomingHolidays.length < 2) {
      return;
    }

    setCurrentHolidayIndex((prev) => (prev + 1) % upcomingHolidays.length);
  };

  const goToPreviousHoliday = () => {
    if (upcomingHolidays.length < 2) {
      return;
    }

    setCurrentHolidayIndex((prev) => (prev - 1 + upcomingHolidays.length) % upcomingHolidays.length);
  };

  const loadAttendanceToday = useCallback(async () => {
    if (!currentEmployeeId) {
      return;
    }

    try {
      const response = await getTodayAttendance();
      const today = response?.today || null;
      const settings = response?.settings || null;
      setAttendanceToday(today);
      setAttendanceDay(response?.date || today?.attendanceDay || "");
      setAttendanceSettings(settings);
      setAttendanceIsWorkDay(response?.isWorkDay === true);

      const dayKey = response?.date || today?.attendanceDay;
      const checkInDismissed = window.localStorage.getItem(getAttendancePromptKey(currentEmployeeId, "check-in", dayKey)) === "true";
      const checkOutDismissed = window.localStorage.getItem(getAttendancePromptKey(currentEmployeeId, "check-out", dayKey)) === "true";
      const snoozedUntil = Number(window.localStorage.getItem(getCheckoutSnoozeKey(currentEmployeeId, dayKey)) || 0);
      const checkoutDue =
        !!today?.checkInAt &&
        !today?.checkOutAt &&
        hasReachedScheduleTime(settings?.checkOutTime);

      setShowAttendancePrompt(!!response?.canCheckIn && !checkInDismissed);
      setShowCheckoutPrompt(!response?.canCheckIn && checkoutDue && !checkOutDismissed && snoozedUntil <= Date.now());
    } catch (requestError) {
      const message = requestError?.message || requestError?.response?.data?.message || "";
      setAttendanceFeedback({
        type: "error",
        title: "Attendance unavailable",
        message: Array.isArray(message) ? message.join(", ") : message || "Unable to load today's attendance.",
      });
      setShowAttendancePrompt(false);
      setShowCheckoutPrompt(false);
      setAttendanceIsWorkDay(false);
    }
  }, [currentEmployeeId]);

  useEffect(() => {
    const timer = window.setInterval(() => setCheckoutReminderTick(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!currentEmployeeId || isCurrentlyOnLeave || !attendanceIsWorkDay || !attendanceToday?.checkInAt || attendanceToday?.checkOutAt || !hasReachedScheduleTime(attendanceSettings?.checkOutTime)) return;

    const dayKey = attendanceDay || getLocalDayKey();
    const snoozedUntil = Number(window.localStorage.getItem(getCheckoutSnoozeKey(currentEmployeeId, dayKey)) || 0);
    const dismissed = window.localStorage.getItem(getAttendancePromptKey(currentEmployeeId, "check-out", dayKey)) === "true";
    if (dismissed || snoozedUntil > Date.now()) return;

    setShowCheckoutPrompt(true);
    const notificationKey = getCheckoutNotificationKey(currentEmployeeId, dayKey);
    if (notificationPermission === "granted" && window.localStorage.getItem(notificationKey) !== "true") {
      const notification = new Notification("Time to clock out", {
        body: "Your scheduled clock-out time has arrived. Open CeHR to clock out or snooze the reminder.",
        icon: "/logo192.png",
        tag: `cehr-checkout-${currentEmployeeId}-${dayKey}`,
        requireInteraction: true,
      });
      notification.onclick = () => {
        window.focus();
        setShowCheckoutPrompt(true);
        notification.close();
      };
      window.localStorage.setItem(notificationKey, "true");
    }
  }, [attendanceDay, attendanceIsWorkDay, attendanceSettings?.checkOutTime, attendanceToday?.checkInAt, attendanceToday?.checkOutAt, checkoutReminderTick, currentEmployeeId, isCurrentlyOnLeave, notificationPermission]);

  const enableCheckoutNotifications = async () => {
    if (!("Notification" in window)) {
      setAttendanceFeedback({ type: "error", title: "Notifications unavailable", message: "This browser does not support desktop notifications." });
      return;
    }
    const permission = await Notification.requestPermission();
    setNotificationPermission(permission);
    setAttendanceFeedback({
      type: permission === "granted" ? "success" : "error",
      title: permission === "granted" ? "Clock-out reminders enabled" : "Notifications not enabled",
      message: permission === "granted" ? "You will receive a browser reminder when it is time to clock out." : "Allow notifications in your browser settings to receive clock-out reminders.",
    });
  };

  useEffect(() => {
    loadAttendanceToday();
  }, [loadAttendanceToday]);

  const dismissAttendancePrompt = () => {
    if (currentEmployeeId) {
      window.localStorage.setItem(getAttendancePromptKey(currentEmployeeId, "check-in", attendanceDay), "true");
    }
    setShowAttendancePrompt(false);
  };

  const dismissCheckoutPrompt = () => {
    if (currentEmployeeId) {
      window.localStorage.setItem(getAttendancePromptKey(currentEmployeeId, "check-out", attendanceDay), "true");
    }
    setShowCheckoutPrompt(false);
  };

  const snoozeCheckoutPrompt = () => {
    const dayKey = attendanceDay || getLocalDayKey();
    const snoozedUntil = Date.now() + CHECKOUT_SNOOZE_MINUTES * 60 * 1000;
    window.localStorage.setItem(getCheckoutSnoozeKey(currentEmployeeId, dayKey), `${snoozedUntil}`);
    window.localStorage.removeItem(getCheckoutNotificationKey(currentEmployeeId, dayKey));
    setShowCheckoutPrompt(false);
    setCheckoutReminderTick(Date.now());
  };

  const submitDashboardCheckIn = async () => {
    try {
      setAttendanceActionLoading(true);
      setAttendanceFeedback(null);
      const payload = await getFreshLocationPayload(attendanceSettings?.geofenceEnabled);
      const result = await checkInAttendance(payload);
      setShowAttendancePrompt(false);
      await loadAttendanceToday();
      setAttendanceFeedback({
        type: "success",
        title: "Clocked in",
        message: result?.pointsAwarded > 0
          ? `Your attendance has been recorded successfully. +${result.pointsAwarded} points awarded.`
          : "Your attendance has been recorded successfully.",
      });
    } catch (requestError) {
      const message = requestError?.message || requestError?.response?.data?.message || "Unable to clock in.";
      setAttendanceFeedback({
        type: "error",
        title: "Clock-in failed",
        message: Array.isArray(message) ? message.join(", ") : message,
      });
    } finally {
      setAttendanceActionLoading(false);
    }
  };

  const submitDashboardCheckOut = async () => {
    try {
      setAttendanceActionLoading(true);
      setAttendanceFeedback(null);
      const payload = await getFreshLocationPayload(attendanceSettings?.geofenceEnabled);
      await checkOutAttendance(payload);
      setShowCheckoutPrompt(false);
      await loadAttendanceToday();
      setAttendanceFeedback({
        type: "success",
        title: "Clocked out",
        message: "Your clock-out has been recorded successfully.",
      });
    } catch (requestError) {
      const message = requestError?.message || requestError?.response?.data?.message || "Unable to clock out.";
      setAttendanceFeedback({
        type: "error",
        title: "Clock-out failed",
        message: Array.isArray(message) ? message.join(", ") : message,
      });
    } finally {
      setAttendanceActionLoading(false);
    }
  };

  const HolidayItem = ({ date, title, flag }) => (
    <div className="flex w-full items-center gap-3 px-3 py-3 sm:px-4">
      <div className="shrink-0 rounded-md bg-blue-100 p-2">
        <img src={calendarIcon} alt="calendar" className="h-5 w-5 object-contain" />
      </div>

      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900">
          {flag ? `${flag} ` : ""}
          {date}
        </p>
        <p className="truncate text-sm text-gray-500">{title}</p>
      </div>
    </div>
  );

  useEffect(() => {
    dashboardDataRef.current = {
      birthdayCelebrants,
      workAnniversaryCelebrants,
      recentRequests,
      recentEngagementPosts,
      upcomingHolidays,
      recentNotifications,
    };
  }, [
    birthdayCelebrants,
    workAnniversaryCelebrants,
    recentRequests,
    recentEngagementPosts,
    upcomingHolidays,
    recentNotifications,
  ]);

  useEffect(() => {
    let active = true;

    const loadLiveDashboard = async ({ silent = false } = {}) => {
      if (!silent && !cachedSnapshot) {
        setLoading(true);
      }

      if (silent) {
        setRefreshing(true);
      }

      setError("");

      try {
        const apiBaseUrl = getApiBaseUrl();
        const engagementRequest =
          apiBaseUrl && currentEmployeeId
            ? axios.get(`${apiBaseUrl}/organizations/engagement/posts`, {
                params: {
                  viewer: "employee",
                  employeeId: currentEmployeeId,
                  page: 1,
                  limit: 2,
                },
              })
            : Promise.resolve({ data: { posts: [] } });

        const [
          birthdayResult,
          anniversaryResult,
          requestResult,
          engagementResult,
          upcomingHolidaysResult,
          notificationsResult,
        ] = await Promise.allSettled([
          getCurrentMonthBirthdays({ page: 1, limit: 6 }),
          getCurrentMonthAnniversaries({ page: 1, limit: 6 }),
          listRequestApplications({ page: 1, limit: 5 }),
          engagementRequest,
          listUpcomingHolidays({ limit: 3 }),
          listMyNotifications({ page: 1, limit: 3 }),
        ]);

        if (!active) {
          return;
        }

        const failedRequests = [
          birthdayResult,
          anniversaryResult,
          requestResult,
          engagementResult,
          upcomingHolidaysResult,
          notificationsResult,
        ].filter((result) => result.status === "rejected");

        const birthdayResponse = getSettledValue(birthdayResult);
        const anniversaryResponse = getSettledValue(anniversaryResult);
        const requestResponse = getSettledValue(requestResult);
        const engagementResponse = getSettledValue(engagementResult);
        const upcomingHolidaysResponse = getSettledValue(upcomingHolidaysResult);
        const notificationsResponse = getSettledValue(notificationsResult);

        const birthdays =
          birthdayResult.status === "fulfilled" && Array.isArray(birthdayResponse?.celebrants)
            ? birthdayResponse.celebrants.map((staff) => ({
              id: staff.id,
              name: getFullName(staff),
              role: getRoleLabel(staff),
              day: staff.day,
              photo: resolveImage(staff.profilePhotoUrl),
            }))
            : dashboardDataRef.current.birthdayCelebrants;

        const anniversaries =
          anniversaryResult.status === "fulfilled" && Array.isArray(anniversaryResponse?.celebrants)
            ? anniversaryResponse.celebrants.map((staff) => ({
              id: staff.id,
              name: getFullName(staff),
              role: getRoleLabel(staff),
              day: staff.day,
              yearsOfService: staff.yearsOfService,
              photo: resolveImage(staff.profilePhotoUrl),
            }))
            : dashboardDataRef.current.workAnniversaryCelebrants;

        const requests =
          requestResult.status === "fulfilled" && Array.isArray(requestResponse?.applications)
            ? requestResponse.applications.map((application) => ({
              id: application.id,
              requestName: application.requestType?.name || "Request",
              requesterName: application.requesterName || application.applicantName || currentEmployeeName || "-",
              reliever: application.relieverName || "Not assigned",
              daysLeft: "-",
              dateRange: application.submittedAt
                ? new Date(application.submittedAt).toLocaleDateString()
                : "-",
              status: `${application.status || "Pending"}`,
            }))
            : dashboardDataRef.current.recentRequests;

        const engagementPosts =
          engagementResult.status === "fulfilled" && Array.isArray(engagementResponse?.data?.posts)
            ? engagementResponse.data.posts.slice(0, 2).map((post) => ({
              id: post.id,
              title: post.title || "Untitled post",
              body: getExcerpt(post.body),
              image: getEngagementImage(post),
              isPublic: post.isEnabled !== false,
              postedBy: post.createdByName || "Admin",
            }))
            : dashboardDataRef.current.recentEngagementPosts;

        const holidays =
          upcomingHolidaysResult.status === "fulfilled" && Array.isArray(upcomingHolidaysResponse?.holidays)
            ? upcomingHolidaysResponse.holidays.map((holiday) => ({
              id: holiday.id,
              date: formatHolidayDate(holiday.startAt),
              name: holiday.holidayName || "Holiday",
              country: holiday.country || "",
              flag: holiday.flagEmoji || "",
            }))
            : dashboardDataRef.current.upcomingHolidays;

        const notifications =
          notificationsResult.status === "fulfilled" && Array.isArray(notificationsResponse?.notifications)
            ? notificationsResponse.notifications.map((notification) => ({
              id: notification.id,
              title: notification.title || "Notification",
              type: notification.type || "general",
              message: notification.message || notification.title || "Notification",
              sentAt: notification.sentAt,
              deliveredAt: notification.deliveredAt,
              createdAt: notification.createdAt,
              senderEmail: notification.senderEmail,
              actionUrl: notification.actionUrl,
              actionLabel: notification.actionLabel,
              time: formatRelativeTime(getNotificationDate(notification)),
              isRead: notification.isRead !== false,
            }))
            : dashboardDataRef.current.recentNotifications;

        setBirthdayCelebrants(birthdays);
        setWorkAnniversaryCelebrants(anniversaries);
        setRecentRequests(requests);
        setRecentEngagementPosts(engagementPosts);
        setUpcomingHolidays(holidays);
        setRecentNotifications(notifications);
        setCurrentHolidayIndex(0);

        writeSessionCache(DASHBOARD_CACHE_KEY, {
          birthdayCelebrants: birthdays,
          workAnniversaryCelebrants: anniversaries,
          recentRequests: requests,
          recentEngagementPosts: engagementPosts,
          upcomingHolidays: holidays,
          recentNotifications: notifications,
        });

        if (failedRequests.length === 6) {
          setError("Unable to load live dashboard data.");
        }
      } catch (requestError) {
        if (!active) {
          return;
        }

        const message =
          requestError?.response?.data?.message ||
          "Unable to load live dashboard data.";
        setError(Array.isArray(message) ? message.join(", ") : message);
      } finally {
        if (active) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    loadLiveDashboard({ silent: true });

    const onFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadLiveDashboard({ silent: true });
      }
    };

    window.addEventListener("focus", onFocusRefresh);
    document.addEventListener("visibilitychange", onFocusRefresh);

    return () => {
      active = false;
      window.removeEventListener("focus", onFocusRefresh);
      document.removeEventListener("visibilitychange", onFocusRefresh);
    };
  }, [cachedSnapshot, currentEmployeeEmail, currentEmployeeId, currentEmployeeName]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] px-3 py-4 font-Outfit dashboard-enter sm:p-6">
      <div className="mb-4 flex items-center justify-between gap-3 sm:mb-5">
        <div>
          <p className="text-2xl font-bold tracking-tight text-[#071C50] sm:text-xl sm:text-black">Overview</p>
          <p className="text-xs text-slate-500 sm:hidden">Your work snapshot for today.</p>
        </div>
        {refreshing ? (
          <span className="rounded-full border border-slate-100 bg-white px-3 py-1 text-[11px] font-medium text-slate-500 shadow-sm sm:hidden">
            Refreshing
          </span>
        ) : null}
      </div>
      {error ? (
        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
          {error}
        </div>
      ) : null}
      {currentEmployeeId && attendanceSettings && attendanceIsWorkDay && !isCurrentlyOnLeave && !attendanceToday?.checkInAt && !showAttendancePrompt ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#DCE3FF] bg-white px-4 py-3 shadow-sm">
          <div>
            <p className="text-sm font-semibold text-[#071C50]">You are set to clock in for today</p>
            <p className="text-xs text-slate-500">Use the quick action now or open Attendance from the sidebar.</p>
          </div>
          <button
            type="button"
            onClick={submitDashboardCheckIn}
            disabled={attendanceActionLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-[#1234CD] px-4 py-2 text-sm font-medium text-white disabled:bg-slate-300"
          >
            <LogIn className="h-4 w-4" />
            {attendanceActionLoading ? "Clocking in..." : "Clock in"}
          </button>
        </div>
      ) : null}
      {currentEmployeeId && attendanceSettings && attendanceIsWorkDay && !isCurrentlyOnLeave && attendanceToday?.checkInAt && !attendanceToday?.checkOutAt && hasReachedScheduleTime(attendanceSettings?.checkOutTime) && !showCheckoutPrompt ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 shadow-sm">
          <div>
            <p className="text-sm font-semibold text-[#071C50]">You are due to clock out</p>
            <p className="text-xs text-slate-500">Your scheduled clock-out time has passed. Confirm clock-out when you are done for the day.</p>
          </div>
          <button
            type="button"
            onClick={() => setShowCheckoutPrompt(true)}
            disabled={attendanceActionLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-[#1234CD] px-4 py-2 text-sm font-medium text-white disabled:bg-slate-300"
          >
            <LogOut className="h-4 w-4" />
            Clock out
          </button>
        </div>
      ) : null}
      {currentEmployeeId && attendanceSettings && attendanceIsWorkDay && !isCurrentlyOnLeave && notificationPermission === "default" ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
          <div><p className="text-sm font-semibold text-[#071C50]">Never miss clock-out time</p><p className="text-xs text-slate-500">Enable browser notifications for an automatic reminder at your scheduled clock-out time.</p></div>
          <button type="button" onClick={enableCheckoutNotifications} className="rounded-lg border border-[#1234CD] bg-white px-4 py-2 text-sm font-semibold text-[#1234CD]">Enable reminder</button>
        </div>
      ) : null}
      <div className="space-y-4 sm:space-y-8">
        <div className="grid grid-cols-1 gap-4 sm:gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(340px,2fr)]">
          <div className="relative mt-0 flex min-h-0 min-w-0 flex-col gap-4 overflow-hidden rounded-2xl border border-slate-100 bg-white p-4 text-[#071C50] shadow-sm dashboard-soft-hover dashboard-enter dashboard-enter-delay-1 sm:min-h-0 sm:rounded-3xl sm:border-0 sm:bg-gradient-to-br sm:from-[#1233CC] sm:via-[#00BAFF] sm:to-[#071C50] sm:p-6 sm:text-white sm:shadow-[0_18px_38px_-25px_rgba(42,18,84,0.85)] md:grid md:grid-cols-[minmax(0,1.2fr)_minmax(220px,0.8fr)] md:items-start md:gap-6 xl:p-8 xl:gap-8">
            <div className="absolute inset-x-0 top-0 h-1 bg-[#1234CD] sm:hidden" />
            <div className="absolute -top-16 -right-16 hidden h-48 w-48 rounded-full bg-white/10 blur-2xl sm:block" />
            <div className="absolute -bottom-20 -left-12 hidden h-56 w-56 rounded-full bg-[#9B7CCF]/25 blur-3xl sm:block" />

            <div className="z-10 flex min-w-0 w-full flex-col text-left">
              <div className="mb-3 flex items-center justify-between gap-3 sm:mb-0 sm:block">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#1234CD] sm:text-xs sm:text-indigo-100/80">
                  Employee Snapshot
                </p>
                <Link
                  to="/profile"
                  className="inline-flex items-center gap-1 rounded-full bg-[#EEF3FF] px-3 py-1 text-xs font-semibold text-[#1234CD] sm:hidden"
                >
                  Profile
                  <FiExternalLink className="h-3.5 w-3.5" />
                </Link>
              </div>
              <h2 className="mb-4 mt-1 hidden text-xl font-semibold sm:mb-6 sm:mt-2 sm:block sm:text-2xl">My Profile</h2>

              <div className="flex items-center gap-3 sm:items-start sm:gap-6">
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-white bg-slate-50 p-1 shadow-[0_14px_30px_-22px_rgba(7,28,80,0.7)] ring-1 ring-slate-100 sm:h-36 sm:w-36 sm:rounded-3xl sm:border-white/30 sm:bg-white/10 sm:shadow-lg sm:ring-0 sm:backdrop-blur-sm">
                  <img
                    src={employeeProfile.photo || profilePlaceholder}
                    alt="Profile"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = profilePlaceholder;
                    }}
                    className="h-full w-full rounded-xl object-cover sm:rounded-2xl"
                  />
                </div>

                <div className="min-w-0">
                  <h1 className="truncate text-xl font-semibold leading-tight sm:text-3xl">
                    {profileLoading
                      ? "Loading profile..."
                      : employeeProfile.name}
                  </h1>
                  <p className="mt-1 truncate text-sm text-slate-500 sm:text-base sm:text-indigo-100">
                    {employeeProfile.title}
                  </p>
                  <div className="mt-3 inline-flex max-w-full items-center gap-2 rounded-full border border-[#DCE3FF] bg-[#EEF3FF] px-3 py-1.5 text-xs font-medium text-[#1234CD] sm:mt-4 sm:border-white/25 sm:bg-white/15 sm:text-indigo-50">
                    <FiUser className="h-3.5 w-3.5" />
                    <span className="truncate">{employeeProfile.department}</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 grid w-full min-w-0 grid-cols-2 gap-2 sm:mt-5">
                <div className="min-w-0 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 sm:border-white/15 sm:bg-white/10 sm:text-indigo-50">
                  <div className="mb-1.5 flex items-center gap-2 text-[11px] text-slate-500 sm:text-indigo-200">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-white text-[#1234CD] shadow-sm sm:bg-white/10 sm:text-indigo-100 sm:shadow-none">
                      <FiHash className="h-3.5 w-3.5" />
                    </span>
                    Employee ID
                  </div>
                  <span className="block truncate font-medium">{employeeProfile.employeeId}</span>
                </div>
                <div className="min-w-0 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 sm:border-white/15 sm:bg-white/10 sm:px-3 sm:py-2 sm:text-indigo-50">
                  <div className="mb-1.5 flex items-center gap-2 text-[11px] text-slate-500 sm:mb-1 sm:gap-1.5 sm:text-indigo-200">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-white text-[#1234CD] shadow-sm sm:h-auto sm:w-auto sm:bg-transparent sm:text-indigo-100 sm:shadow-none">
                      <FiLayers className="h-3.5 w-3.5" />
                    </span>
                    Level
                  </div>
                  <span className="block truncate font-medium" title={employeeProfile.level}>{employeeProfile.level}</span>
                </div>
                <div className="hidden min-w-0 rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-sm text-indigo-50 sm:block">
                  <div className="mb-1 flex items-center gap-1.5 text-[11px] text-indigo-200">
                    <FiPhone className="h-3.5 w-3.5" />
                    Phone
                  </div>
                  <span className="block truncate font-medium">{employeeProfile.phone}</span>
                </div>
                <div className="min-w-0 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 sm:border-white/15 sm:bg-white/10 sm:text-indigo-50">
                  <div className="mb-1.5 flex items-center gap-2 text-[11px] text-slate-500 sm:text-indigo-200">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-white text-[#1234CD] shadow-sm sm:bg-white/10 sm:text-indigo-100 sm:shadow-none">
                      <FiCalendar className="h-3.5 w-3.5" />
                    </span>
                    Joined
                  </div>
                  <span className="block truncate font-medium">{employeeProfile.joined}</span>
                </div>
              </div>
            </div>

            <div className="z-10 hidden min-w-0 w-full border-t border-white/25 pt-4 sm:block md:border-l md:border-t-0 md:pl-6 md:pt-0 xl:pl-8">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-indigo-100/80 sm:mb-3 sm:text-xs">
                About
              </p>
              <p className="line-clamp-3 max-w-md text-sm leading-relaxed text-indigo-100 md:line-clamp-none md:text-base">
                {employeeProfile.summary}
              </p>
              <div className="mt-4 inline-flex max-w-full items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-sm text-indigo-50 sm:mt-5">
                <FiMail className="h-4 w-4 text-indigo-200" />
                <span className="max-w-[240px] truncate">
                  {employeeProfile.email}
                </span>
              </div>
              <Link
                to="/profile"
                className="mt-4 inline-flex w-fit items-center gap-2 rounded-xl border border-white/20 bg-white px-4 py-2 text-sm font-semibold text-[#1233CC] transition hover:bg-indigo-50"
              >
                Open profile
                <FiExternalLink className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <aside className="hidden min-w-0 flex-col rounded-3xl border border-slate-100 bg-white p-6 shadow-[0_20px_40px_-28px_rgba(11,23,64,0.45)] dashboard-soft-hover dashboard-enter dashboard-enter-delay-2 xl:flex">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#1234CD]">Work overview</p>
                <h2 className="mt-1 text-xl font-bold text-[#071C50]">Today at a glance</h2>
              </div>
              <span className="rounded-full bg-[#EEF3FF] px-3 py-1 text-xs font-semibold text-[#1234CD]">{new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3" onClick={(event) => { let card = event.target; while (card && card.parentElement !== event.currentTarget) card = card.parentElement; if (!card) return; const index = Array.from(event.currentTarget.children).indexOf(card); const routes = ["/attendance", "/requests", "/notification", "/company-settings/holidays", "/engagement", "/company-settings/holidays"]; if (routes[index]) navigate(routes[index]); }}>
              <div className="rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
                <p className="text-xs font-medium text-slate-500">Attendance</p>
                <p className="mt-2 text-sm font-bold text-[#071C50]">{isCurrentlyOnLeave ? "On leave" : attendanceToday?.checkOutAt ? "Clocked out" : attendanceToday?.checkInAt ? "Clocked in" : attendanceIsWorkDay ? "Not clocked in" : "No shift today"}</p>
              </div>
              <div className="rounded-2xl border border-amber-100 bg-amber-50/60 p-4">
                <p className="text-xs font-medium text-slate-500">Active requests</p>
                <p className="mt-2 text-2xl font-bold text-[#071C50]">{recentRequests.filter((request) => request.status === "Pending").length}</p>
              </div>
              <div className="rounded-2xl border border-violet-100 bg-violet-50/60 p-4">
                <p className="text-xs font-medium text-slate-500">Unread updates</p>
                <p className="mt-2 text-2xl font-bold text-[#071C50]">{recentNotifications.filter((notification) => notification.isRead === false).length}</p>
              </div>
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4">
                <p className="text-xs font-medium text-slate-500">Next holiday</p>
                <p className="mt-2 truncate text-sm font-bold text-[#071C50]" title={upcomingHolidays[0]?.name}>{upcomingHolidays[0]?.name || "None scheduled"}</p>
                <p className="mt-1 text-[11px] text-slate-500">{upcomingHolidays[0] ? formatHolidayDate(upcomingHolidays[0].date) : "—"}</p>
              </div>
              <div className="hidden rounded-2xl border border-cyan-100 bg-cyan-50/60 p-4 lg:block">
                <p className="text-xs font-medium text-slate-500">Engagement posts</p>
                <p className="mt-2 text-2xl font-bold text-[#071C50]">{recentEngagementPosts.length}</p>
                <p className="mt-1 text-[11px] text-slate-500">Latest updates for you</p>
              </div>
              <div className="hidden rounded-2xl border border-rose-100 bg-rose-50/60 p-4 lg:block">
                <p className="text-xs font-medium text-slate-500">Upcoming events</p>
                <p className="mt-2 text-2xl font-bold text-[#071C50]">{upcomingHolidays.length}</p>
                <p className="mt-1 text-[11px] text-slate-500">Scheduled holidays</p>
              </div>
            </div>

          </aside>

          <TodoPanel
            compact
            showTeam={false}
            compactLimit={4}
            viewMoreTo="/productivity?section=tasks"
            title="Todos"
            className="min-w-0 rounded-2xl border border-slate-100 shadow-sm dashboard-soft-hover dashboard-enter dashboard-enter-delay-2 sm:rounded-3xl sm:shadow-[0_20px_40px_-28px_rgba(11,23,64,0.45)] xl:col-span-2"
          />
        </div>

        <Link
          to="/employee-dashboard/career-grading"
          className="group flex w-full items-center justify-between gap-3 rounded-xl border border-[#D9E3FF] bg-white px-4 py-3 text-sm font-semibold text-[#071C50] shadow-sm transition hover:border-[#1234CD] hover:bg-[#F7F9FF] sm:px-5"
        >
          <span className="truncate">Professional career progression</span>
          <ChevronRight className="h-4 w-4 shrink-0 text-[#1234CD] transition-transform group-hover:translate-x-0.5" />
        </Link>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          <div className="space-y-3 dashboard-enter dashboard-enter-delay-1 sm:space-y-4">
            <AvatarStackCard
              title={`${monthLabel} birthday celebrants`}
              images={birthdayCelebrants
                .slice(0, 3)
                .map((staff) => staff.photo)}
              extraCount={Math.max(birthdayCelebrants.length - 3, 0)}
              to="/company-settings/staff-birthdays"
            />

            <AvatarStackCard
              title={`${monthLabel} work anniversaries`}
              images={workAnniversaryCelebrants
                .slice(0, 3)
                .map((staff) => staff.photo)}
              extraCount={Math.max(workAnniversaryCelebrants.length - 3, 0)}
              to="/company-settings/work-anniversaries"
            />

            <div className="w-full rounded-2xl border border-slate-100 bg-white p-4 shadow-sm dashboard-soft-hover sm:max-w-xl sm:border-0 sm:p-5">
              <div className="mb-3 flex items-center justify-between sm:mb-4">
                <button
                  type="button"
                  onClick={goToPreviousHoliday}
                  disabled={upcomingHolidays.length < 2}
                  className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-600 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Previous holiday"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <h2 className="text-sm font-semibold tracking-tight text-[#071C50] sm:text-lg sm:tracking-normal">
                  Upcoming Holiday
                </h2>

                <button
                  type="button"
                  onClick={goToNextHoliday}
                  disabled={upcomingHolidays.length < 2}
                  className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-600 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Next holiday"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              <div className="flex flex-col overflow-hidden rounded-xl bg-[#EFF7FC] sm:flex-row sm:items-center sm:divide-x">
                {visibleHolidays.length > 0 ? (
                  visibleHolidays.map((holiday) => (
                    <HolidayItem
                      key={holiday.id}
                      date={holiday.date}
                      title={holiday.name}
                      flag={holiday.flag}
                    />
                  ))
                ) : (
                  <div className="px-4 py-5 text-sm text-gray-500">
                    No upcoming holidays yet.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm dashboard-soft-hover dashboard-enter dashboard-enter-delay-2 sm:rounded-3xl sm:p-6">
            <div className="mb-3 flex items-center justify-between gap-3 sm:mb-4">
              <h3 className="text-base font-bold tracking-tight text-[#071C50]">Recent Notifications</h3>
              <Link to="/notification" className="text-xs font-semibold text-[#1234CD] hover:underline">View more</Link>
            </div>
            {recentNotifications.length > 0 ? (
              recentNotifications.map((notification) => (
                <div
                  role="button"
                  tabIndex={0}
                  key={notification.id}
                  onClick={() => {
                    setSelectedNotification(notification);
                    if (notification.isRead === false) {
                      setRecentNotifications((items) => items.map((item) => item.id === notification.id ? { ...item, isRead: true } : item));
                      markMyNotificationAsRead(notification.id).catch(() => {});
                    }
                  }}
                  onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setSelectedNotification(notification); } }}
                  className="flex w-full items-center gap-3 rounded-xl border-b border-gray-50 p-2.5 text-left transition-colors hover:bg-slate-50 last:border-0 sm:gap-4 sm:p-3"
                >
                  <NotificationTypeIcon notification={notification} className="h-9 w-9 sm:h-10 sm:w-10" />
                  <div className="flex-1">
                    <p className="text-xs font-medium line-clamp-2">
                      {notification.message}
                    </p>
                    <span className="text-[10px] text-gray-400">
                      {notification.time}
                    </span>
                  </div>
                  {getNotificationActionUrl(notification) ? <button type="button" onClick={(event) => { event.stopPropagation(); window.location.href = getNotificationActionUrl(notification); }} className="rounded-md border border-[#1234CD] px-2 py-1 text-xs font-semibold text-[#1234CD] hover:bg-blue-50">{getNotificationActionLabel(notification)}</button> : null}
                  {!notification.isRead ? <div className="w-2 h-2 rounded-full bg-green-500" /> : null}
                </div>
              ))
            ) : (
              <p className="py-6 text-center text-sm text-gray-500">
                No notifications yet.
              </p>
            )}
          </div>

          <NotificationDetailsModal
            notification={selectedNotification}
            onClose={() => setSelectedNotification(null)}
            onDelete={async (notification) => {
              await deleteMyNotification(notification.id);
              setRecentNotifications((items) => items.filter((item) => item.id !== notification.id));
            }}
          />

          <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm dashboard-soft-hover dashboard-enter dashboard-enter-delay-3 sm:rounded-3xl sm:p-6">
            <div className="mb-3 flex justify-between sm:mb-4">
              <h3 className="text-base font-bold tracking-tight text-[#071C50]">Recent Engagement</h3>
              <Link
                to="/engagement"
                className="rounded-full bg-[#EEF3FF] px-3 py-1 text-xs font-semibold text-[#1234CD] sm:bg-transparent sm:px-0 sm:py-0 sm:text-indigo-500"
              >
                View all
              </Link>
            </div>
            <div className="space-y-4">
              {recentEngagementPosts.length > 0 ? (
                recentEngagementPosts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/engagement/${encodeURIComponent(post.id)}`}
                    className="block rounded-xl border border-slate-100 bg-slate-50 p-3 transition hover:bg-[#EFF7FC] sm:rounded-lg sm:border-0 sm:bg-[#EFF7FC66] sm:p-2"
                  >
                    <div className="mb-2 flex items-center justify-between gap-2 text-xs">
                      <p className="min-w-0 truncate">
                        <span className=" font-medium">Posted By</span>:{" "}
                        {post.postedBy}
                      </p>

                      <span className=" bg-[#DBF6FF] text-xs p-1 rounded-lg">
                        {post.isPublic ? "Public" : "Hidden"}
                      </span>
                    </div>
                    <div className="flex gap-3">
                      {post.image ? (
                        <img
                          src={post.image}
                          alt={post.title}
                          className="h-16 w-16 rounded-lg object-cover sm:h-20 sm:w-20"
                        />
                      ) : (
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-[#DBF6FF] text-lg font-bold text-[#1234CD] sm:h-20 sm:w-20">
                          {post.title.slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="line-clamp-1 text-base font-bold sm:text-lg">
                          {post.title}
                        </p>
                        <p className="text-xs text-gray-400 line-clamp-2">
                          {post.body || "No description available."}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="rounded-xl bg-slate-50 p-4 text-sm text-gray-500 sm:bg-[#EFF7FC66]">
                  No recent engagement posts yet.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm dashboard-soft-hover dashboard-enter dashboard-enter-delay-2 sm:rounded-3xl sm:p-8">
          <div className="mb-4 flex items-center justify-between gap-3 sm:mb-8">
            <h3 className="text-lg font-bold tracking-tight text-[#071C50] sm:text-xl sm:text-black">My Requests</h3>
            <Link
              to="/requests"
              className="rounded-xl bg-[#1234CD] px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 sm:px-6"
            >
              Send Request
            </Link>
          </div>

          <div className="hidden">
            {loading ? (
              <p className="py-6 text-center text-sm text-gray-500">
                Loading live requests...
              </p>
            ) : recentRequests.length > 0 ? (
              recentRequests.slice(0, 5).map((row) => (
                <Link
                  key={row.id}
                  to={`/requests/request-details?applicationId=${encodeURIComponent(row.id)}`}
                  className="block rounded-2xl border border-slate-100 bg-slate-50 p-4 transition hover:border-[#DCE3FF] hover:bg-white"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-slate-900">{row.requestName}</p>
                      <p className="mt-1 text-xs text-slate-500">{row.dateRange}</p>
                    </div>
                    <span className={`shrink-0 text-xs font-semibold ${statusClass(row.status)}`}>
                      {row.status}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-500">
                    <p className="col-span-2">
                      <span className="font-medium text-slate-700">Requester:</span> {row.requesterName}
                    </p>
                    <p>
                      <span className="font-medium text-slate-700">Reliever:</span> {row.reliever}
                    </p>
                    <p>
                      <span className="font-medium text-slate-700">Days:</span> {row.daysLeft}
                    </p>
                  </div>
                </Link>
              ))
            ) : (
              <p className="py-6 text-center text-sm text-gray-500">
                No live request records yet.
              </p>
            )}
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-100">
            <table className="w-full min-w-[860px] text-left">
              <thead>
                <tr className="text-gray-400 text-sm uppercase tracking-wider">
                  <th className="pb-4 font-medium">Leave Type</th>
                  <th className="pb-4 font-medium">Requester</th>
                  <th className="pb-4 font-medium">Reliever</th>
                  <th className="pb-4 font-medium">Days Left</th>
                  <th className="pb-4 font-medium">Date Range</th>
                  <th className="pb-4 font-medium">Status</th>
                  <th className="pb-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {loading ? (
                  <tr>
                    <td
                      className="py-6 text-center text-gray-500"
                      colSpan={7}
                    >
                      Loading live requests...
                    </td>
                  </tr>
                ) : recentRequests.length > 0 ? (
                  recentRequests.slice(0, 5).map((row) => (
                    <tr
                      key={row.id}
                      className="border-t border-gray-50"
                    >
                      <td className="py-4">{row.requestName}</td>
                      <td className="py-4">{row.requesterName}</td>
                      <td className="py-4">{row.reliever}</td>
                      <td className="py-4 ">{row.daysLeft}</td>
                      <td className="py-4">{row.dateRange}</td>
                      <td className="py-4">
                        <span
                          className={`font-semibold ${statusClass(row.status)}`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className="py-4 text-right">
                        <ViewActionButton
                          to={`/requests/request-details?applicationId=${encodeURIComponent(row.id)}`}
                          label={`View ${row.requestName || "request"} details`}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      className="py-6 text-center text-gray-500"
                      colSpan={7}
                    >
                      No live request records yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {refreshing ? (
            <p className="mt-3 text-xs text-gray-400">
              Refreshing latest dashboard data...
            </p>
          ) : null}
        </div>
      </div>
      {showAttendancePrompt && !isCurrentlyOnLeave ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 pt-6 sm:pt-10">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#1234CD]">Attendance</p>
                <h2 className="mt-1 text-xl font-semibold text-[#071C50]">Ready to clock in?</h2>
              </div>
              <button type="button" onClick={dismissAttendancePrompt} className="rounded-md p-2 text-slate-500 hover:bg-slate-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-3 text-sm text-slate-500">
              Clock in now to record your attendance quickly. If you cancel, a reminder will stay at the top of your dashboard.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={dismissAttendancePrompt} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">
                Cancel
              </button>
              <button
                type="button"
                onClick={submitDashboardCheckIn}
                disabled={attendanceActionLoading}
                className="inline-flex items-center gap-2 rounded-lg bg-[#1234CD] px-4 py-2 text-sm font-medium text-white disabled:bg-slate-300"
              >
                <LogIn className="h-4 w-4" />
                {attendanceActionLoading ? "Clocking in..." : "Clock in"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {showCheckoutPrompt && !isCurrentlyOnLeave ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 pt-6 sm:pt-10">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#1234CD]">Attendance</p>
                <h2 className="mt-1 text-xl font-semibold text-[#071C50]">Ready to clock out?</h2>
              </div>
              <button type="button" onClick={dismissCheckoutPrompt} className="rounded-md p-2 text-slate-500 hover:bg-slate-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-3 text-sm text-slate-500">
              Your scheduled clock-out time has passed. Confirm clock-out to close your attendance for today.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={snoozeCheckoutPrompt} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">
                Snooze {CHECKOUT_SNOOZE_MINUTES} min
              </button>
              <button
                type="button"
                onClick={submitDashboardCheckOut}
                disabled={attendanceActionLoading}
                className="inline-flex items-center gap-2 rounded-lg bg-[#1234CD] px-4 py-2 text-sm font-medium text-white disabled:bg-slate-300"
              >
                <LogOut className="h-4 w-4" />
                {attendanceActionLoading ? "Clocking out..." : "Clock out"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <AttendanceFeedbackModal
        feedback={attendanceFeedback}
        onClose={() => setAttendanceFeedback(null)}
      />
    </div>
  );
};

function ContractDashboard({ employee }) {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState('');
  const canViewProjects = employee?.modulePermissions?.includes('productivity');
  useEffect(() => {
    if (!canViewProjects) return;
    let active = true;
    const base = getApiBaseUrl();
    Promise.all([
      axios.get(`${base}/organizations/productivity/dashboard`),
      axios.get(`${base}/organizations/todos?scope=mine&limit=20`),
    ]).then(([projectResponse, taskResponse]) => {
      if (!active) return;
      setProjects(projectResponse.data?.projects || []);
      setTasks(taskResponse.data?.todos || []);
    }).catch((requestError) => { if (active) setError(requestError?.response?.data?.message || 'Unable to load your projects.'); });
    return () => { active = false; };
  }, [canViewProjects]);
  return <main className="space-y-6 p-4 font-Nunito sm:p-6">
    <div><h1 className="text-2xl font-bold text-slate-900">Welcome, {employee?.firstName || 'team member'}</h1><p className="text-slate-500">Your contract workspace</p></div>
    {error && <p role="alert" className="rounded-xl bg-red-50 p-3 text-red-700">{error}</p>}
    {canViewProjects ? <><section><h2 className="mb-3 text-lg font-bold">Assigned projects</h2><div className="grid gap-3 md:grid-cols-2">{projects.map((project) => <Link key={project.id} to={`/productivity/project/${project.id}`} className="rounded-xl border bg-white p-4"><strong>{project.title}</strong><p className="mt-1 text-sm text-slate-500">{project.status}</p></Link>)}</div>{!projects.length && <p className="text-slate-500">No projects assigned yet.</p>}</section>
      <section><h2 className="mb-3 text-lg font-bold">My tasks</h2><div className="space-y-2">{tasks.map((task) => <div key={task.id} className="rounded-xl border bg-white p-3"><strong>{task.title}</strong><span className="ml-3 text-sm text-slate-500">{task.status}</span></div>)}</div>{!tasks.length && <p className="text-slate-500">No tasks assigned yet.</p>}</section></> : <p className="rounded-xl border bg-white p-4 text-slate-600">Your administrator or department head can assign a project to open your work area.</p>}
  </main>;
}

function ContractAwareDashboard() {
  const { currentEmployee, loading } = useCurrentEmployee();
  if (loading) return <div className="p-6">Loading dashboard...</div>;
  return currentEmployee?.isContractor ? <ContractDashboard employee={currentEmployee} /> : <Dashhboard />;
}

export default ContractAwareDashboard;
